import React, { useState } from 'react';
import { Link, generatePath, useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveSite from '../../../hooks/useActiveSite';
import ToDoAlertCard from './ToDoAlertCard';
import { receiveRemoveSiteTodo, removeSiteTodo } from '../../../actions/sites';
import snackbarHelper from '../../../helpers/snackbarHelper';
import OverlayLoader from '../../../components/OverlayLoader';

const ToDoIrregularRescuesBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={routes.mobileToDo} label="To-do" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="Irregular Rescues" />
    </Breadcrumbs>
  </Box>
);

const ToDoIrregularRescues = () => {
  const location = useLocation();
  const history = useHistory();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const [currentCardIndex, setCurrentCardIndex] = useState(location.state?.toDoCardIndex || 0);
  const [loading, setLoading] = useState();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const irregularRescues = sdTodos?.irregularRescues || [];

  const handleSkipClick = () => {
    setCurrentCardIndex(currentCardIndex + 1);
  };

  const handleRemoveClick = (todo, handleCardOut, animationTime) => {
    setLoading(true);
    Bluebird.try(() => dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleReviewClick = (todo, handleCardOut, animationTime) => {
    if (!todo?.details?.id) return;
    setLoading(true);
    Bluebird.try(() => dispatch(removeSiteTodo(activeSite.id, todo.entity_id, todo.entity_type, todo.type, false)))
      .then(() => {
        handleCardOut();
        setTimeout(() => {
          dispatch(receiveRemoveSiteTodo(activeSite.id, todo.entity_id, todo.type));
          history.push(generatePath(routes.rescue_edit, { rescueId: todo.details.id }));
        }, animationTime);
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  if (!irregularRescues.length || currentCardIndex + 1 > irregularRescues.length) {
    return (
      <>
        <ToDoIrregularRescuesBreadcrumbs />
        <Box
          sx={{
            width: '100%',
            height: 'fit-content',
            backgroundColor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            paddingLeft: 2,
            paddingRight: 2,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              '-webkit-transform': 'translate(-50%, -50%)',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography color="textSecondary" variant="h5">
              You're all caught up, great work!
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <OverlayLoader loading={loading}>
      <ToDoIrregularRescuesBreadcrumbs />

      <Box
        sx={{
          width: '100%',
          height: 'fit-content',
          backgroundColor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          {irregularRescues.map((rescue, index) => (
            <ToDoAlertCard
              key={rescue.entity_id}
              rescue={rescue}
              current={index === currentCardIndex}
              next={irregularRescues.length <= index ? null : index === currentCardIndex + 1}
              onRemoveClick={handleRemoveClick}
              onSkipClick={handleSkipClick}
              onReviewClick={handleReviewClick}
              reviewLabel="Review"
              isConfirm
              content={
                <span>
                  Pickup from{' '}
                  <b>
                    {rescue?.details?.location?.length <= 20
                      ? rescue?.details?.location
                      : `${rescue?.details?.location?.substr(0, 20)}...`}
                  </b>{' '}
                  to{' '}
                  <b>
                    {rescue?.details?.dropoff_location?.length <= 20
                      ? rescue?.details?.dropoff_location
                      : `${rescue?.details?.dropoff_location?.substr(0, 20)}...`}
                  </b>{' '}
                  on <b>{moment(rescue?.details?.date).format('ddd MMM Do')}</b>
                  has an unusual weight: <b>{rescue?.details?.calculated_lbs} lbs</b>. Please confirm or review!
                </span>
              }
            />
          ))}
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default ToDoIrregularRescues;
